/**
 * Code snippet generators for different LLM providers.
 * These snippets help users get started with Weave tracing.
 */

export const makeOpenAICodeSnippet = (
  entity: string,
  project: string
) => String.raw`# Ensure your dependencies are installed with:
# pip install openai weave

# Find your OpenAI API key at: https://platform.openai.com/api-keys
# Ensure that your OpenAI API key is available at:
# os.environ['OPENAI_API_KEY'] = "<your_openai_api_key>"

import os
import weave
from openai import OpenAI

# Find your wandb API key at: https://wandb.ai/authorize
weave.init('${entity ? `${entity}/` : ''}${project}') # 🐝

@weave.op # 🐝 Decorator to track requests
def create_completion(message: str) -> str:
    client = OpenAI()
    response = client.chat.completions.create(
        model="gpt-5",
        messages=[
            {"role": "system", "content": "You are a helpful assistant."},
            {"role": "user", "content": message}
        ],
    )
    return response.choices[0].message.content

message = "Tell me a joke."
create_completion(message)`;

export const makeAnthropicCodeSnippet = (
  entity: string,
  project: string
) => String.raw`# Ensure your dependencies are installed with:
# pip install anthropic weave

# Find your Anthropic API key at: https://console.anthropic.com/settings/keys
# Ensure that your Anthropic API key is available at:
# os.environ['ANTHROPIC_API_KEY'] = "<your_anthropic_api_key>"

import os
import weave
from anthropic import Anthropic

# Find your wandb API key at: https://wandb.ai/authorize
weave.init('${entity ? `${entity}/` : ''}${project}') # 🐝

@weave.op # 🐝 Decorator to track requests
def create_completion(message: str) -> str:
    client = Anthropic()
    response = client.messages.create(
        model="claude-opus-4-1-20250805",
        system="You are a helpful assistant.",
        max_tokens=120,
        messages=[
            {"role": "user", "content": message}
        ]
    )
    return response.content[0].text

message = "Tell me a joke."
create_completion(message)`;

export const makeWandbInferenceCodeSnippet = (
  entity: string,
  project: string
) => String.raw`# Ensure your dependencies are installed with:
# pip install openai weave

# Find your wandb API key at: https://wandb.ai/authorize
# Ensure that your wandb API key is available at:
# os.environ['WANDB_API_KEY'] = "<your_wandb_api_key>"

import os
import weave
from openai import OpenAI

# Find your wandb API key at: https://wandb.ai/authorize
weave.init('${entity ? `${entity}/` : ''}${project}') # 🐝

@weave.op # 🐝 Decorator to track requests
def create_completion(message: str) -> str:
  client = OpenAI(
      base_url='https://api.inference.wandb.ai/v1',
      api_key=os.environ['WANDB_API_KEY'], # 🔑 Your wandb API key
      project='${entity ? `${entity}/` : ''}${project}',
  )
  response = client.chat.completions.create(
      model="OpenPipe/Qwen3-14B-Instruct",
      messages=[
          {"role": "system", "content": "You are a helpful assistant."},
          {"role": "user", "content": message}
      ],
  )
  return response.choices[0].message.content
message = "Tell me a joke."
create_completion(message)
`;

export const makeOpenAIEvaluationSnippet = (
  entity: string,
  project: string
) => String.raw`# Ensure your dependencies are installed with:
# pip install openai pandas weave

# Find your OpenAI API key at: https://platform.openai.com/api-keys
# Ensure that your OpenAI API key is available at:
# os.environ['OPENAI_API_KEY'] = "<your_openai_api_key>"

import asyncio
import os
import re
from textwrap import dedent

import openai
import weave


class JsonModel(weave.Model):
    prompt: weave.Prompt = weave.StringPrompt(
        dedent("""
You are an assistant that answers questions about JSON data provided by the user. The JSON data represents structured information of various kinds, and may be deeply nested. In the first user message, you will receive the JSON data under a label called 'context', and a question under a label called 'question'. Your job is to answer the question with as much accuracy and brevity as possible. Give only the answer with no preamble. You must output the answer in XML format, between <answer> and </answer> tags.
""")
    )
    model: str = "gpt-4.1-nano"
    _client: openai.OpenAI

    def __init__(self):
        super().__init__()
        self._client = openai.OpenAI()

    @weave.op
    def predict(self, context: str, question: str) -> str:
        response = self._client.chat.completions.create(
            model=self.model,
            messages=[
                {"role": "system", "content": self.prompt.format()},
                {
                    "role": "user",
                    "content": f"Context: {context}\nQuestion: {question}",
                },
            ],
        )
        assert response.choices[0].message.content is not None
        return response.choices[0].message.content


@weave.op
def correct_answer_format(answer: str, output: str) -> dict[str, bool]:
    parsed_output = re.search(r"<answer>(.*?)</answer>", output, re.DOTALL)
    if parsed_output is None:
        return {"correct_answer": False, "correct_format": False}
    return {"correct_answer": parsed_output.group(1) == answer, "correct_format": True}


if __name__ == "__main__":
    if not os.environ.get('OPENAI_API_KEY'):
        print("OPENAI_API_KEY is not set - make sure to export it in your environment or assign it in this script")
        exit(1)

    # Find your wandb API key at: https://wandb.ai/authorize
    weave.init("${entity ? `${entity}/` : ''}${project}")

    jsonqa = weave.Dataset.from_uri(
        "weave:///wandb/json-qa/object/json-qa:v3"
    ).to_pandas()

    model = JsonModel()

    eval = weave.Evaluation(
        name="json-qa-eval",
        dataset=weave.Dataset.from_pandas(jsonqa),
        scorers=[correct_answer_format],
    )

    asyncio.run(eval.evaluate(model))`;

export const makeAnthropicEvaluationSnippet = (
  entity: string,
  project: string
) => String.raw`# Ensure your dependencies are installed with:
# pip install anthropic pandas weave

# Find your Anthropic API key at: https://console.anthropic.com/settings/keys
# Ensure that your Anthropic API key is available at:
# os.environ['ANTHROPIC_API_KEY'] = "<your_anthropic_api_key>"

import asyncio
import os
import re
from textwrap import dedent

import weave
from anthropic import Anthropic


class JsonModel(weave.Model):
    prompt: weave.Prompt = weave.StringPrompt(
        dedent("""
You are an assistant that answers questions about JSON data provided by the user. The JSON data represents structured information of various kinds, and may be deeply nested. In the first user message, you will receive the JSON data under a label called 'context', and a question under a label called 'question'. Your job is to answer the question with as much accuracy and brevity as possible. Give only the answer with no preamble. You must output the answer in XML format, between <answer> and </answer> tags.
""")
    )
    model: str = "claude-opus-4-1-20250805"
    _client: Anthropic

    def __init__(self):
        super().__init__()
        self._client = Anthropic()

    @weave.op
    def predict(self, context: str, question: str) -> str:
        response = self._client.messages.create(
            model=self.model,
            system=self.prompt.format(),
            max_tokens=120,
            messages=[
                {"role": "user", "content": f"Context: {context}\nQuestion: {question}"}
            ],
        )
        return response.content[0].text


@weave.op
def correct_answer_format(answer: str, output: str) -> dict[str, bool]:
    parsed_output = re.search(r"<answer>(.*?)</answer>", output, re.DOTALL)
    if parsed_output is None:
        return {"correct_answer": False, "correct_format": False}
    return {"correct_answer": parsed_output.group(1) == answer, "correct_format": True}


if __name__ == "__main__":

    # Find your wandb API key at: https://wandb.ai/authorize
    weave.init("${entity ? `${entity}/` : ''}${project}")

    if not os.environ.get('ANTHROPIC_API_KEY'):
        print("ANTHROPIC_API_KEY is not set - make sure to export it in your environment or assign it in this script")
        exit(1)

    jsonqa = weave.Dataset.from_uri(
        "weave:///wandb/json-qa/object/json-qa:v3"
    ).to_pandas()

    model = JsonModel()

    eval = weave.Evaluation(
        name="json-qa-eval",
        dataset=weave.Dataset.from_pandas(jsonqa),
        scorers=[correct_answer_format],
    )

    asyncio.run(eval.evaluate(model))`;

export const makeWandbInferenceEvaluationSnippet = (
  entity: string,
  project: string
) => String.raw`# Ensure your dependencies are installed with:
# pip install openai pandas weave

# Find your wandb API key at: https://wandb.ai/authorize
# Ensure that your wandb API key is available at:
# os.environ['WANDB_API_KEY'] = "<your_wandb_api_key>"

import asyncio
import os
import re
from textwrap import dedent

import weave
from openai import OpenAI


class JsonModel(weave.Model):
    prompt: weave.Prompt = weave.StringPrompt(
        dedent("""
You are an assistant that answers questions about JSON data provided by the user. The JSON data represents structured information of various kinds, and may be deeply nested. In the first user message, you will receive the JSON data under a label called 'context', and a question under a label called 'question'. Your job is to answer the question with as much accuracy and brevity as possible. Give only the answer with no preamble. You must output the answer in XML format, between <answer> and </answer> tags.
""")
    )
    model: str = "OpenPipe/Qwen3-14B-Instruct"
    _client: OpenAI

    def __init__(self):
        super().__init__()
        self._client = OpenAI(
            base_url='https://api.inference.wandb.ai/v1',
            api_key=os.environ['WANDB_API_KEY'],
            project='${entity ? `${entity}/` : ''}${project}',
        )

    @weave.op
    def predict(self, context: str, question: str) -> str:
        response = self._client.chat.completions.create(
            model=self.model,
            messages=[
                {"role": "system", "content": self.prompt.format()},
                {
                    "role": "user",
                    "content": f"Context: {context}\nQuestion: {question}",
                },
            ],
        )
        return response.choices[0].message.content


@weave.op
def correct_answer_format(answer: str, output: str) -> dict[str, bool]:
    parsed_output = re.search(r"<answer>(.*?)</answer>", output, re.DOTALL)
    if parsed_output is None:
        return {"correct_answer": False, "correct_format": False}
    return {"correct_answer": parsed_output.group(1) == answer, "correct_format": True}


if __name__ == "__main__":
    if not os.environ.get('WANDB_API_KEY'):
        print("WANDB_API_KEY is not set - make sure to export it in your environment or assign it in this script")
        exit(1)

    # Find your wandb API key at: https://wandb.ai/authorize
    weave.init("${entity ? `${entity}/` : ''}${project}")

    jsonqa = weave.Dataset.from_uri(
        "weave:///wandb/json-qa/object/json-qa:v3"
    ).to_pandas()

    model = JsonModel()

    eval = weave.Evaluation(
        name="json-qa-eval",
        dataset=weave.Dataset.from_pandas(jsonqa),
        scorers=[correct_answer_format],
    )

    asyncio.run(eval.evaluate(model))`;

export type LLMProvider = 'openai' | 'anthropic' | 'wandb-inference';
export type SnippetType = 'trace' | 'evaluation';

export const getCodeSnippetForProvider = (
  provider: LLMProvider,
  entity: string,
  project: string
): string => {
  switch (provider) {
    case 'openai':
      return makeOpenAICodeSnippet(entity, project);
    case 'anthropic':
      return makeAnthropicCodeSnippet(entity, project);
    case 'wandb-inference':
      return makeWandbInferenceCodeSnippet(entity, project);
    default:
      return makeOpenAICodeSnippet(entity, project);
  }
};

export const getEvaluationSnippetForProvider = (
  provider: LLMProvider,
  entity: string,
  project: string
): string => {
  switch (provider) {
    case 'openai':
      return makeOpenAIEvaluationSnippet(entity, project);
    case 'anthropic':
      return makeAnthropicEvaluationSnippet(entity, project);
    case 'wandb-inference':
      return makeWandbInferenceEvaluationSnippet(entity, project);
    default:
      return makeOpenAIEvaluationSnippet(entity, project);
  }
};
